import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  useTheme,
  IconButton,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Close as CloseIcon, TrendingUp } from '@mui/icons-material';
import { format } from 'date-fns';
import { usePortfolioPerformance } from '../hooks/usePortfolioPerformance';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { PerformanceMetrics } from '../types/performance';

interface PerformanceChartDialogProps {
  open: boolean;
  onClose: () => void;
  portfolioId: string | null;
  portfolioName: string;
}

const PerformanceChartDialog: React.FC<PerformanceChartDialogProps> = ({
  open,
  onClose,
  portfolioId,
  portfolioName,
}) => {
  const theme = useTheme();
  const { performance, loading, error } = usePortfolioPerformance(open ? portfolioId : null);

  const chartData = useMemo(() => {
    if (!performance?.dataPoints) return [];

    return performance.dataPoints.map((point) => ({
      date: point.date.getTime(),
      dateDisplay: format(point.date, 'MMM d'),
      value: point.cumulativeReturn,
      percent: point.cumulativeReturnPercent,
      trades: point.tradeCount,
    }));
  }, [performance]);

  const { minValue, maxValue } = useMemo(() => {
    if (chartData.length === 0) return { minValue: 0, maxValue: 100 };
    const values = chartData.map((d) => d.value);
    const min = Math.min(...values, 0);
    const max = Math.max(...values, 0);
    const padding = Math.abs(max - min) * 0.1 || 100;
    return {
      minValue: min - padding,
      maxValue: max + padding,
    };
  }, [chartData]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="body2" fontWeight="medium">
            {data.dateDisplay}
          </Typography>
          <Typography
            variant="body2"
            color={data.value >= 0 ? 'success.main' : 'error.main'}
          >
            {data.value >= 0 ? '+' : ''}
            {formatCurrency(data.value)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data.percent >= 0 ? '+' : ''}
            {data.percent.toFixed(2)}% | {data.trades} trades
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Portfolio Performance: {portfolioName}
          </Typography>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={400}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={400}
          >
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {!loading && !error && performance && (
          <Box>
            {performance.metrics.totalTrades === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight={400}
              >
                <TrendingUp sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No completed trades yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Close some positions to see performance metrics
                </Typography>
              </Box>
            ) : (
              <>
                {/* Metrics Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <MetricCard
                      title="Total Return"
                      value={formatCurrency(performance.metrics.totalReturn)}
                      subtitle={formatPercent(performance.metrics.totalReturnPercent)}
                      positive={performance.metrics.totalReturn >= 0}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <MetricCard
                      title="Avg Monthly"
                      value={formatCurrency(performance.metrics.avgMonthlyReturn)}
                      subtitle={formatPercent(performance.metrics.avgMonthlyReturnPercent)}
                      positive={performance.metrics.avgMonthlyReturn >= 0}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <MetricCard
                      title="Win Rate"
                      value={`${performance.metrics.winRate.toFixed(0)}%`}
                      subtitle={`${performance.metrics.winCount}/${performance.metrics.totalTrades}`}
                      positive={performance.metrics.winRate >= 50}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <MetricCard
                      title="Profit Factor"
                      value={
                        performance.metrics.profitFactor === null
                          ? 'N/A'
                          : `${performance.metrics.profitFactor.toFixed(2)}x`
                      }
                      subtitle={
                        performance.metrics.profitFactor === null
                          ? 'No losses'
                          : 'Gross profit / loss'
                      }
                      positive={
                        performance.metrics.profitFactor === null ||
                        performance.metrics.profitFactor >= 1
                      }
                    />
                  </Grid>
                </Grid>

                {/* Chart */}
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Cumulative Returns Over Time
                  </Typography>
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart
                      data={chartData}
                      margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorPositive" x1="0" y1="0" x2="0" y2="1">
                          <stop
                            offset="5%"
                            stopColor={theme.palette.success.main}
                            stopOpacity={0.3}
                          />
                          <stop
                            offset="95%"
                            stopColor={theme.palette.success.main}
                            stopOpacity={0}
                          />
                        </linearGradient>
                        <linearGradient id="colorNegative" x1="0" y1="1" x2="0" y2="0">
                          <stop
                            offset="5%"
                            stopColor={theme.palette.error.main}
                            stopOpacity={0.3}
                          />
                          <stop
                            offset="95%"
                            stopColor={theme.palette.error.main}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={theme.palette.divider}
                      />
                      <XAxis
                        dataKey="dateDisplay"
                        stroke={theme.palette.text.secondary}
                        style={{ fontSize: 12 }}
                      />
                      <YAxis
                        tickFormatter={(value) => {
                          const absVal = Math.abs(value);
                          const sign = value < 0 ? '-' : '';
                          if (absVal >= 1000000) return `${sign}$${(absVal / 1000000).toFixed(1)}M`;
                          if (absVal >= 1000) return `${sign}$${(absVal / 1000).toFixed(1)}K`;
                          return `${sign}$${absVal.toFixed(0)}`;
                        }}
                        stroke={theme.palette.text.secondary}
                        style={{ fontSize: 12 }}
                        domain={[minValue, maxValue]}
                        width={60}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <ReferenceLine
                        y={0}
                        stroke={theme.palette.text.secondary}
                        strokeDasharray="3 3"
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke={
                          performance.metrics.totalReturn >= 0
                            ? theme.palette.success.main
                            : theme.palette.error.main
                        }
                        strokeWidth={2}
                        fill={
                          performance.metrics.totalReturn >= 0
                            ? 'url(#colorPositive)'
                            : 'url(#colorNegative)'
                        }
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Paper>

                {/* Additional Metrics */}
                <AdditionalMetrics metrics={performance.metrics} />
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

interface MetricCardProps {
  title: string;
  value: string;
  subtitle: string;
  positive: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  positive,
}) => {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography
        variant="h5"
        color={positive ? 'success.main' : 'error.main'}
        fontWeight="bold"
      >
        {positive && !value.startsWith('-') && !value.startsWith('N') ? '+' : ''}
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {positive && !subtitle.startsWith('-') && !subtitle.startsWith('N') ? '+' : ''}
        {subtitle}
      </Typography>
    </Paper>
  );
};

interface AdditionalMetricsProps {
  metrics: PerformanceMetrics;
}

const AdditionalMetrics: React.FC<AdditionalMetricsProps> = ({ metrics }) => {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Additional Metrics
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Expectancy
            </Typography>
            <Typography
              variant="body1"
              fontWeight="medium"
              color={metrics.expectancy >= 0 ? 'success.main' : 'error.main'}
            >
              {metrics.expectancy >= 0 ? '+' : ''}
              {formatCurrency(metrics.expectancy)} per trade
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Max Drawdown
            </Typography>
            <Typography variant="body1" fontWeight="medium" color="error.main">
              -{formatCurrency(metrics.maxDrawdown)} (-{formatPercent(metrics.maxDrawdownPercent)})
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Months Active
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {metrics.monthsActive}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Average Win
            </Typography>
            <Typography variant="body1" fontWeight="medium" color="success.main">
              +{formatCurrency(metrics.avgWin)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Average Loss
            </Typography>
            <Typography variant="body1" fontWeight="medium" color="error.main">
              -{formatCurrency(metrics.avgLoss)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Trades
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {metrics.totalTrades}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PerformanceChartDialog;
